import React, { FC, ReactNode, useEffect, useMemo } from "react";
import { GoogleMap as ReactGoogleMap } from "@react-google-maps/api";
import { PageLoader } from "@vilocnv/allsetra-core";
import { MapContainer, MapWrapper } from "../../Map.styled";
import { useMapState } from "./MapStateContext";
import { googleMapStyles } from "app/data/constants";
import {
  privateRideMapStyles,
  privateRideMapOptions
} from "app/data/constants/maps/privateRideMapStyles";
import {
  DashboardMapLoaderWrapper,
  DashboardMapWrapper
} from "components/maps/DashboardMap/DashboardMap.styled";
import { CircularProgress } from "@mui/material";

const GoogleMap: FC<{ children?: ReactNode }> = ({ children }) => {
  const {
    showSatelliteMode,
    mapRef,
    zoom,
    height,
    isLoaded,
    loadError,
    centerCoords,
    onMapLoad,
    onTilesLoaded,
    isLoading,
    isPrivateRide,
    isObjectDetailsPage
  } = useMapState();

  useEffect(() => {
    if (!mapRef) return;

    mapRef.setMapTypeId(
      showSatelliteMode
        ? google.maps.MapTypeId.HYBRID
        : google.maps.MapTypeId.ROADMAP
    );
  }, [mapRef, showSatelliteMode]);

  const AMSTERDAM_COORDS = { lat: 52.3676, lng: 4.9041 };

  const mapCenter = useMemo(() => {
    if (isPrivateRide && isObjectDetailsPage) {
      return AMSTERDAM_COORDS;
    }
    return centerCoords;
  }, [isPrivateRide, isObjectDetailsPage, centerCoords]);

  const mapOptions = useMemo(() => {
    if (isPrivateRide) {
      return {
        ...privateRideMapOptions,
        styles: privateRideMapStyles
      };
    }

    return {
      styles: googleMapStyles
    };
  }, [isPrivateRide]);

  if (!isLoaded) return <PageLoader />;
  if (loadError) return <div>Map cannot be loaded right now, sorry.</div>;

  return (
    <MapWrapper>
      {isLoading && (
        <DashboardMapLoaderWrapper>
          <CircularProgress sx={{ color: "#1976d2" }} />
        </DashboardMapLoaderWrapper>
      )}
      <DashboardMapWrapper allObjectsLoading={isLoading}>
        <MapContainer height={height}>
          <ReactGoogleMap
            mapTypeId={
              showSatelliteMode
                ? google.maps.MapTypeId.HYBRID
                : google.maps.MapTypeId.ROADMAP
            }
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={mapCenter}
            zoom={zoom}
            onLoad={onMapLoad}
            onTilesLoaded={onTilesLoaded}
            options={mapOptions}
          >
            {children}
          </ReactGoogleMap>
        </MapContainer>
      </DashboardMapWrapper>
    </MapWrapper>
  );
};

export default React.memo(GoogleMap);
